import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API, BACKEND_URL } from "../App";
import { Download, FileText, Search, Filter, Folder, File } from "lucide-react";

const DocumentsPage = () => {
  const { t } = useTranslation('operations');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectorFilter, setSectorFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchDocuments = async () => {
    try {
      let url = `${API}/documents`;
      const params = [];
      if (sectorFilter !== "all") params.push(`sector=${sectorFilter}`);
      if (categoryFilter !== "all") params.push(`category=${categoryFilter}`);
      if (params.length) url += `?${params.join("&")}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [sectorFilter, categoryFilter]);

  const handleDownload = async (docId, fileName) => {
    try {
      const response = await axios.get(`${API}/documents/download/${docId}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading document:", error);
      alert("Error downloading document. Please try again.");
    }
  };

  const filteredDocs = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.doc_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group documents by sector
  const groupedDocs = filteredDocs.reduce((acc, doc) => {
    if (!acc[doc.sector]) acc[doc.sector] = [];
    acc[doc.sector].push(doc);
    return acc;
  }, {});

  const sectorLabels = {
    "life-sciences": "Life Sciences",
    "aerospace": "Aerospace",
    "automotive": "Automotive",
    "industrial": "Industrial / EHS",
    "iot": "IoT / Cybersecurity",
    "quality-consulting": "Quality Consulting",
    "validation-testing": "Validation & Testing",
    "training-programs": "Training Programs",
    "regulatory-strategy": "Regulatory Strategy",
    "supplier-qm": "Supplier QM",
    "audit-management": "Audit Management",
    "design-controls": "Design Controls",
    "change-control": "Change Control",
    "complaint-handling": "Complaint Handling",
    "data-integrity": "Data Integrity",
    "csv-gamp": "CSV / GAMP 5"
  };

  const sectorColors = {
    "life-sciences": "#4ADE80",
    "aerospace": "#FBBF24",
    "automotive": "#F87171",
    "industrial": "#8B95A5",
    "iot": "#E8A838",
    "quality-consulting": "#36A9E1",
    "validation-testing": "#A78BFA",
    "training-programs": "#34D399",
    "regulatory-strategy": "#FB923C",
    "supplier-qm": "#F472B6",
    "audit-management": "#38BDF8",
    "design-controls": "#818CF8",
    "change-control": "#FB7185",
    "complaint-handling": "#FDBA74",
    "data-integrity": "#67E8F9",
    "csv-gamp": "#C084FC"
  };

  const categoryLabels = {
    "audit": "Audit Reports",
    "testing": "Testing Protocols",
    "protocol": "R&D Protocols",
    "schedule": "Schedules",
    "agreement": "Agreements"
  };

  return (
    <QMSLayout title={t('documents.title')}>
      {/* Header */}
      <div className="mb-6">
        <p className="text-[#8B95A5]">{t('documents.librarySubtitle')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="doc-stats">
        {Object.entries(sectorLabels).map(([key, label]) => {
          const count = documents.filter(d => d.sector === key).length;
          return (
            <div 
              key={key}
              className="qms-card p-4 cursor-pointer hover:border-[#2D313D]"
              onClick={() => setSectorFilter(sectorFilter === key ? "all" : key)}
              data-testid={`sector-stat-${key}`}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-[3px] flex items-center justify-center"
                  style={{ backgroundColor: `${sectorColors[key]}20` }}
                >
                  <Folder size={20} style={{ color: sectorColors[key] }} />
                </div>
                <div>
                  <p className="text-[#8B95A5] text-xs">{label}</p>
                  <p className="font-['Syne'] text-xl font-bold text-white">{count}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6" data-testid="doc-filters">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B95A5]" size={18} />
          <input
            type="text"
            placeholder={t('documents.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="qms-input pl-10"
            data-testid="doc-search"
          />
        </div>

        {/* Sector Filter Pills */}
        <div className="filter-pills">
          <button
            onClick={() => setSectorFilter("all")}
            className={`filter-pill ${sectorFilter === "all" ? "active" : ""}`}
          >
            {t('documents.allSectors')}
          </button>
          {Object.entries(sectorLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSectorFilter(key)}
              className={`filter-pill ${sectorFilter === key ? "active" : ""}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Category Filter */}
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="qms-select w-auto"
          data-testid="category-filter"
        >
          <option value="all">All Categories</option>
          {Object.entries(categoryLabels).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="text-center py-12 text-[#8B95A5]">{t('documents.loadingDocuments')}</div>
      ) : filteredDocs.length === 0 ? (
        <div className="text-center py-12 text-[#8B95A5]">{t('documents.noDocuments')}</div>
      ) : (
        <div className="space-y-6" data-testid="documents-list">
          {Object.entries(groupedDocs).map(([sector, docs]) => (
            <div key={sector} className="qms-card">
              <div 
                className="qms-card-header flex items-center gap-3"
                style={{ borderLeftColor: sectorColors[sector], borderLeftWidth: "3px" }}
              >
                <Folder size={18} style={{ color: sectorColors[sector] }} />
                <span>{sectorLabels[sector]}</span>
                <span className="badge badge-gray ml-auto">{docs.length} {t('documents.templates')}</span>
              </div>
              <div className="qms-card-body p-0">
                <div className="divide-y divide-[rgba(255,255,255,0.07)]">
                  {docs.map((doc) => (
                    <div 
                      key={doc.doc_id}
                      className="flex items-center gap-4 p-4 hover:bg-[rgba(30,58,95,0.2)] transition-colors"
                      data-testid={`doc-row-${doc.doc_id}`}
                    >
                      <div className="w-10 h-10 bg-[#181B23] rounded-[3px] flex items-center justify-center">
                        <FileText size={20} className="text-[#36A9E1]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{doc.title}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[#38BDF8] text-xs font-mono">{doc.doc_id}</span>
                          <span className="badge badge-blue text-xs capitalize">{doc.category}</span>
                          <span className="text-[#8B95A5] text-xs">v{doc.version}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(doc.doc_id, doc.file_name)}
                        className="btn-ghost py-2 px-4"
                        data-testid={`download-${doc.doc_id}`}
                      >
                        <Download size={16} />
                        <span className="hidden sm:inline">{t('documents.download')}</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Document Info */}
      <div className="mt-8 p-6 bg-[#181B23] rounded-[3px] border border-[rgba(255,255,255,0.07)]" data-testid="doc-info">
        <h3 className="font-['Syne'] text-lg font-semibold text-white mb-4">{t('documents.libraryInfo')}</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-[#8B95A5]">
          <div>
            <p className="mb-2"><strong className="text-white">{t('documents.totalTemplates')}:</strong> {documents.length}</p>
            <p className="mb-2"><strong className="text-white">{t('documents.format')}:</strong> {t('documents.formatValue')}</p>
            <p><strong className="text-white">{t('documents.versionControl')}:</strong> {t('documents.versionControlDesc')}</p>
          </div>
          <div>
            <p className="mb-2"><strong className="text-white">{t('documents.categories')}:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>{t('documents.auditReports')}</li>
              <li>{t('documents.qualificationProtocols')}</li>
              <li>{t('documents.rdProtocols')}</li>
              <li>{t('documents.qualityAgreements')}</li>
            </ul>
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

export default DocumentsPage;
